/**
 * 판매 취소 창 — 판매 현황의 [취소] 가 연다(백엔드 V장 "취소 하나로", 2026-11-02).
 * 사유 둘(고객 취소 · 기타 — 감액은 2026-11-03 뺌, 판매 상세의 단가 고치기로) 중 하나 + 글 한 줄(기타면 필수) + 품목 줄마다 "다시 등록" 체크.
 * 저장은 하지 않는다 — 답으로 부르는 쪽이 취소하고, 체크한 품목이 있으면 판매 등록 화면으로 간다.
 * 창은 document.body 에 붙였다가 닫으면 뗀다(이유 창 꼴 · mg-* 틀을 같이 쓴다).
 */
import type { EClosedReason, SalesOrderCancelInput, SalesOrderDetail, SalesOrderItemView } from '../api/client';
import { E_CLOSED_REASONS } from '../api/client';
import { CLOSED_REASON_WORD } from '../pages/sales-order/words';
import { assert } from '../lib/assert';
import { escapeHtml, fmtNumber } from '../lib/cells';

/** 취소 창의 답 — 보낼 사유 + 다시 등록할 품목 행(quote_item_id, 없으면 []) */
export type SalesCancelChoice = { input: SalesOrderCancelInput; reregister_item_ids: string[] };

const NOTE_REQUIRED_TEXT = '기타 사유는 글을 적어야 합니다';

/** 창을 열고 답을 기다린다. [취소 확정] 이면 답, [닫기] · 바깥 · Esc 면 null */
export async function openSalesCancelDialog(detail: SalesOrderDetail): Promise<SalesCancelChoice | null> {
  const host = document.createElement('div');
  host.innerHTML = renderSalesCancelDialog(detail);
  document.body.appendChild(host);

  return new Promise((resolve) => {
    function close(result: SalesCancelChoice | null): void {
      document.removeEventListener('keydown', handleKey);
      host.remove();
      resolve(result);
    }
    function choiceOrNull(): SalesCancelChoice | null {
      const checked = host.querySelector<HTMLInputElement>('input[name="closed-reason"]:checked');
      assert(checked !== null, '사유 라디오 하나는 처음부터 골라져 있다 — renderSalesCancelDialog 가 첫째를 checked 로 그렸다');
      const reason = closedReasonOf(checked.value);
      const note = noteInputOf(host).value.trim();
      if (reason === 'etc' && note === '') return null;
      const reregister = [...host.querySelectorAll<HTMLInputElement>('input[name="reregister"]:checked')].map((box) => box.value);
      return { input: { closed_reason: reason, closed_note: note === '' ? null : note }, reregister_item_ids: reregister };
    }
    /** 기타인데 글이 비면 [취소 확정] 을 잠그고 붉은 한 줄 */
    function refreshConfirm(): void {
      const bBlocked = choiceOrNull() === null;
      const button = host.querySelector<HTMLButtonElement>('[data-action="sales-cancel-confirm"]');
      const warning = host.querySelector<HTMLElement>('.sc-note-required');
      assert(button !== null && warning !== null, '취소 창은 [취소 확정] 과 글 필수 한 줄을 그렸다 — renderSalesCancelDialog');
      button.disabled = bBlocked;
      warning.hidden = !bBlocked;
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') close(null);
    }
    function handleClick(event: MouseEvent): void {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (target === null) return;
      if (target.dataset.action === 'sales-cancel-close') close(null);
      if (target.dataset.action === 'sales-cancel-confirm') {
        const choice = choiceOrNull();
        if (choice !== null) close(choice);
      }
    }

    host.addEventListener('click', handleClick);
    host.addEventListener('input', refreshConfirm);
    host.addEventListener('change', refreshConfirm);
    document.addEventListener('keydown', handleKey);
    refreshConfirm();
  });
}

/** 창 전체 — 사유 라디오 셋(첫째가 골라진 채) · 글 한 줄 · 품목 체크 · [닫기] [취소 확정] */
function renderSalesCancelDialog(detail: SalesOrderDetail): string {
  const reasons = E_CLOSED_REASONS.map((reason, index) => `
          <label class="so-reason"><input type="radio" name="closed-reason" value="${reason}"${index === 0 ? ' checked' : ''}> ${CLOSED_REASON_WORD[reason]}</label>`).join('');
  const items = detail.items.map(renderReregisterLine).join('');
  return `
    <div class="mg-wrap" role="dialog" aria-label="판매 취소">
      <div class="mg-overlay" data-action="sales-cancel-close"></div>
      <div class="mg-dialog">
        <h3 class="mg-title">판매 취소 — ${escapeHtml(detail.quote_no)}</h3>
        <div class="so-reasons">${reasons}</div>
        <input type="text" class="input sc-note" placeholder="취소 사유 글(기타면 필수)">
        <p class="sc-note-required text-danger" hidden>${NOTE_REQUIRED_TEXT}</p>
        <div class="sc-items">
          <div class="sc-items-head">다시 등록할 품목</div>
          ${items}
        </div>
        <p class="mg-note">묶여 있는 실물은 가용으로 풀립니다. 나간 실물이 있으면 '취소(일부 출고)' 로 남습니다. 체크한 품목은 취소 뒤 판매 등록 화면에 채워집니다.</p>
        <div class="mg-buttons">
          <button type="button" class="btn-ghost" data-action="sales-cancel-close">닫기</button>
          <button type="button" class="btn-primary" data-action="sales-cancel-confirm">취소 확정</button>
        </div>
      </div>
    </div>`;
}

/** 품목 한 줄 — 체크(기본 해제) · 파트넘버 · 디스크립션 · ×수량 · 컨디션 · 나간 실물이 있으면 "나감 n" */
function renderReregisterLine(item: SalesOrderItemView): string {
  const dispatched = item.units.filter((unit) => unit.stock_status === 'dispatched').length;
  return `
          <label class="sc-item">
            <input type="checkbox" name="reregister" value="${escapeHtml(item.quote_item_id)}">
            ${item.option_part === null || item.option_part === '' ? '' : `<span class="mono">${escapeHtml(item.option_part)}</span>`}
            <span class="sc-item-name">${escapeHtml(item.description ?? '')}</span>
            <span class="text-muted">×${fmtNumber(item.quantity)}${item.condition === null ? '' : ` · ${escapeHtml(item.condition)}`}</span>
            ${dispatched === 0 ? '' : `<span class="sc-item-gone">나감 ${fmtNumber(dispatched)}</span>`}
          </label>`;
}

function noteInputOf(host: HTMLElement): HTMLInputElement {
  const input = host.querySelector<HTMLInputElement>('.sc-note');
  assert(input !== null, '취소 창은 사유 글 칸(.sc-note)을 그렸다 — renderSalesCancelDialog');
  return input;
}

/** 라디오 값 → 사유. 우리가 그린 값만 오니 밖이면 있을 수 없는 상태 */
function closedReasonOf(value: string): EClosedReason {
  const reason = E_CLOSED_REASONS.find((candidate) => candidate === value);
  assert(reason !== undefined, `취소 사유 라디오 값 '${value}' 가 E_CLOSED_REASONS 밖이다 — renderSalesCancelDialog 가 그린 값만 온다`);
  return reason;
}
